import { parseCostCenterFile } from "@/apis/custom/customRoutes";
import type { ParsedCostCenterRecord } from "@/apis/gql/types";
import { Label } from "@/components/ui/label";
import { useSession } from "@/lib/auth/auth";
import { cn } from "@/lib/utils";
import { ExclamationTriangleIcon } from "@radix-ui/react-icons";
import { FileText, Loader2, Upload, X } from "lucide-react";
import React, { useEffect, useState } from "react";
import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import type { FormValues } from "../form-schema";

interface CostCenterFileUploadProps {
  label: string;
  accept: string;
  onChange: (file: File | null) => void;
  onCostCentersParsed?: (costCenters: ParsedCostCenterRecord[]) => void;
  value?: File | null;
}

export function CostCenterFileUpload({
  label,
  accept,
  onChange,
  onCostCentersParsed,
  value,
}: CostCenterFileUploadProps) {
  const [dragActive, setDragActive] = React.useState(false);
  const [file, setFile] = React.useState<File | null>(null);
  const [isParsing, setIsParsing] = useState(false);
  const [costCenterCount, setCostCenterCount] = useState<number | null>(null);
  const [parsingError, setParsingError] = useState<string | null>(null);
  const [parsedCostCenters, setParsedCostCenters] = useState<
    ParsedCostCenterRecord[]
  >([]);
  const { data: session } = useSession();
  const { t } = useTranslation();
  const { getValues } = useFormContext<FormValues>();

  // Update local state when value prop changes (for persistence between steps)
  useEffect(() => {
    if (value) {
      setFile(value);
      setParsingError(null);
      // Parse the file when it's set
      parseFile(value);
    } else {
      setFile(null);
      setCostCenterCount(null);
      setParsedCostCenters([]);
      // Don't clear parsingError here - let it persist to show the error message
    }
  }, [value]);

  const parseFile = async (fileToParse: File) => {
    setIsParsing(true);
    setParsingError(null);
    try {
      const response = await parseCostCenterFile(
        fileToParse,
        getValues().companyName,
      );

      if (response?.data && Array.isArray(response.data)) {
        if (response.data.length === 0) {
          // No cost centers found - treat as error
          const errorMessage = t(
            "mandanten_create.cost_center_file_upload.no_cost_centers_found",
            { filename: fileToParse.name },
          );
          setParsingError(errorMessage);
          toast.error(
            t("mandanten_create.cost_center_file_upload.parsing_error"),
          );

          // Remove the file but keep the error state
          setFile(null);
          setCostCenterCount(null);
          setParsedCostCenters([]);
          onChange(null);

          // Reset the file input value
          const fileInput = document.getElementById(
            `file-${label}`,
          ) as HTMLInputElement;
          if (fileInput) {
            fileInput.value = "";
          }
          return;
        }

        setCostCenterCount(response.data.length);
        setParsedCostCenters(response.data);
        toast.success(
          t("mandanten_create.cost_center_file_upload.cost_centers_found", {
            count: response.data.length,
          }),
        );

        // Call the callback with parsed cost centers data
        if (onCostCentersParsed) {
          onCostCentersParsed(response.data);
        }
      } else {
        throw new Error(
          t("mandanten_create.cost_center_file_upload.invalid_server_response"),
        );
      }
    } catch (error) {
      console.error("Error parsing cost center file:", error);
      const errorMessage = t(
        "mandanten_create.cost_center_file_upload.file_processing_error",
        { filename: fileToParse.name },
      );
      setParsingError(errorMessage);
      toast.error(t("mandanten_create.cost_center_file_upload.parsing_error"));

      // Remove the file but keep the error state
      setFile(null);
      setCostCenterCount(null);
      setParsedCostCenters([]);
      onChange(null);

      // Reset the file input value
      const fileInput = document.getElementById(
        `file-${label}`,
      ) as HTMLInputElement;
      if (fileInput) {
        fileInput.value = "";
      }
    } finally {
      setIsParsing(false);
    }
  };

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files?.[0]) {
      const newFile = e.dataTransfer.files?.[0];
      setFile(newFile);
      onChange(newFile);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.preventDefault();
    if (e.target.files?.[0]) {
      const newFile = e.target.files?.[0];

      setFile(newFile);
      onChange(newFile);
    }
  };

  const handleRemoveFile = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setFile(null);
    setCostCenterCount(null);
    setParsedCostCenters([]);
    onChange(null);

    // Reset the file input value
    const fileInput = document.getElementById(
      `file-${label}`,
    ) as HTMLInputElement;
    if (fileInput) {
      fileInput.value = "";
    }
  };

  // Function to format file size
  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Number.parseFloat((bytes / k ** i).toFixed(2)) + " " + sizes[i];
  };

  return (
    <div className="space-y-2">
      <Label>{label}</Label>

      {!file ? (
        // Upload interface when no file is selected
        <div
          className={cn(
            "border-2 border-dashed rounded-lg p-4 text-center cursor-pointer",
            dragActive ? "border-primary" : "border-gray-300",
            "hover:border-primary transition-colors",
          )}
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
        >
          <input
            type="file"
            accept={accept}
            onChange={handleChange}
            className="hidden"
            id={`file-${label}`}
          />
          <label
            htmlFor={`file-${label}`}
            className="cursor-pointer flex flex-col items-center"
          >
            <Upload className="h-6 w-6 mb-2 text-gray-500" />
            <span className="text-sm font-medium">
              <strong>
                {t("mandanten_create.cost_center_file_upload.click_or_drag")}
              </strong>
            </span>
          </label>
        </div>
      ) : (
        // File preview when a file is selected
        <div className="border rounded-lg p-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="bg-gray-100 p-2 rounded">
                <FileText className="h-5 w-5 text-gray-600" />
              </div>
              <div className="flex flex-col">
                <span className="text-sm font-medium truncate-short max-w-[200px]">
                  {file.name}
                </span>
                <span className="text-xs text-gray-500">
                  {formatFileSize(file.size)}
                </span>
                {isParsing && (
                  <div className="flex items-center space-x-2 mt-1">
                    <Loader2 className="h-3 w-3 animate-spin text-blue-500" />
                    <span className="text-xs text-blue-500">
                      {t("mandanten_create.cost_center_file_upload.analyzing")}
                    </span>
                  </div>
                )}
                {costCenterCount !== null && !isParsing && (
                  <span className="text-xs font-medium mt-1 text-green-600">
                    {t(
                      "mandanten_create.cost_center_file_upload.cost_centers_found_count",
                      { count: costCenterCount },
                    )}
                  </span>
                )}
              </div>
            </div>
            <button
              onClick={handleRemoveFile}
              className="p-1 rounded-full hover:bg-gray-100"
              title={t("mandanten_create.cost_center_file_upload.remove_file")}
            >
              <X className="h-4 w-4 text-gray-500" />
            </button>
          </div>
        </div>
      )}

      {/* Cost Centers Preview Box */}
      {parsedCostCenters.length > 0 && (
        <div className="border rounded-lg p-4 bg-gray-50">
          <h4 className="text-sm font-medium text-gray-900 mb-3">
            {t("mandanten_create.cost_center_file_upload.preview_title", {
              count: parsedCostCenters.length,
            })}
          </h4>
          <div className="space-y-2 max-h-40 overflow-y-auto">
            {parsedCostCenters.slice(0, 10).map((costCenter, index) => (
              <div
                key={index}
                className="flex justify-between items-center text-sm bg-white p-2 rounded border"
              >
                <div className="flex-1">
                  <span className="font-medium text-gray-900">
                    {costCenter.identifier}
                  </span>
                  <span className="text-gray-500 ml-2">{costCenter.name}</span>
                </div>
                {costCenter.description && (
                  <span className="text-xs text-gray-400 ml-2 truncate-short max-w-32">
                    {costCenter.description}
                  </span>
                )}
              </div>
            ))}
            {parsedCostCenters.length > 10 && (
              <div className="text-xs text-gray-500 text-center py-2">
                {t("mandanten_create.cost_center_file_upload.and_more", {
                  count: parsedCostCenters.length - 10,
                })}
              </div>
            )}
          </div>
        </div>
      )}

      {parsingError && (
        <div className="mt-2 p-3 border border-red-300 bg-red-50 rounded-md">
          <div className="flex items-center space-x-2 mb-1">
            <ExclamationTriangleIcon className="w-5 h-5 text-red-600 flex-shrink-0" />
            <span className="font-semibold text-red-800 text-sm">
              {t(
                "mandanten_create.cost_center_file_upload.file_processing_error_title",
              )}
            </span>
          </div>
          <div className="text-sm text-red-800 pl-7">{parsingError}</div>
        </div>
      )}
    </div>
  );
}
